import {
  AccountGuid,
  CartItemUsc,
  DEFAULT_BLURB,
  DEFAULT_TAX_ROUNDING_STRATEGY,
  DiscountType,
  DUMMY_GUID,
  DynamicPricingType,
  EstimateV2Status,
  Guid,
  HtmlString,
  InvoiceTerm,
  isFinanceableAmountUsd,
  JobAppointmentGuid,
  JobGuid,
  LocationGuid,
  nextGuid,
  PricebookTaxRateDto,
  R,
  ServiceLocation,
  TaxConfiguration,
} from '@breezy/shared'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { BaseLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner'
import { DiscountPickerDiscount } from '../../components/Pricebook/DiscountMultiPicker'
import { AsyncPhotoData } from '../../components/Upload/AsyncPhotoUpload'
import { StatusTagColor } from '../../elements/StatusTag/StatusTag'
import { EstimateOptionsInsertInput, GetEstimateSubscription } from '../../generated/user/graphql'
import { useCompanyCurrentTaxConfiguration } from '../../hooks/fetch/useCompanyTaxConfiguration'
import { trpc } from '../../hooks/trpc'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useUpsertRecentPricebookItems } from '../../hooks/useUpsertRecentPricebookItems'
import { useWisetackEnabled } from '../../providers/CompanyFinancialConfigWrapper'
import { usePricebook } from '../../providers/PricebookProvider'
import { useExpectedCompanyGuid, usePrincipalUser } from '../../providers/PrincipalUser'
import { StateSetter, useStrictContext } from '../../utils/react-utils'
import { getEffectiveMaintenancePlanSavingsEnabled } from '../Invoices/invoiceUtils'
import { RELEVANT_ESTIMATE_DATA_QUERY, UPDATE_ESTIMATE_MUTATION, UPSERT_ESTIMATE_MUTATION } from './EstimatesFlow.gql'

export type OptionCartItem = CartItemUsc & {
  cartItemGuid: Guid
  seq: number
  savedToPricebook?: boolean
}

export type OptionDiscount = DiscountPickerDiscount

export type OptionWithoutTotal = {
  optionGuid: Guid
  displayName?: string
  descriptionHtml?: HtmlString
  lineItems: OptionCartItem[]
  discounts: OptionDiscount[]
  recommended: boolean
  selected: boolean
  seq: number
  featuredPhotoGuid?: Guid
  featuredPhotoCdnUrl?: string
  taxConfiguration: TaxConfiguration
}

export type Option = OptionWithoutTotal & {
  totalUsc: number
  taxConfiguration: TaxConfiguration
}

export type FetchedEstimate = NonNullable<GetEstimateSubscription['estimatesByPk']>

export type EstimatesContextType = {
  serviceLocation?: Pick<ServiceLocation, 'address' | 'displayName'> | null
  serviceLocationReadError?: boolean
  retryServiceLocation?: () => unknown
  estimateGuid: Guid
  jobGuid: JobGuid
  accountGuid: AccountGuid
  locationGuid: LocationGuid
  jobAppointmentGuid?: JobAppointmentGuid
  realPricebookItemGuidMap: Record<Guid, true>
  disclaimer: string
  defaultInvoiceTerm: InvoiceTerm
  companyName: string
  companyBlurb: string
  companyMaintenancePlanSavingsEnabled?: boolean
  companyHideLineItemPrices?: boolean
  logoUrl: string
  logoPhotoGuid: Guid
  logoPhotoCdnUrl: string
  /* Used for linking a contact to a prequal application in the consumer estimate review page */
  prequalContactGuid?: Guid
}

export const getPhotoRecordsFromEstimate = (estimatePhotos: FetchedEstimate['estimatePhotos']): EstimatePhoto[] =>
  estimatePhotos.map(photo => ({
    photoGuid: photo.photoGuid,
    cdnUrl: photo.photo.cdnUrl,
  }))

export const EstimatesContext = React.createContext<EstimatesContextType | undefined>(undefined)

export type EstimatePointOfContact = {
  firstName: string
  fullName: string
  phoneNumber?: string
  email?: string
}

export type EstimateLocation = {
  name?: string
  line2?: string
  line1: string
  city: string
  stateAbbreviation: string
  zipCode: string
}

export type EstimateDataContextType = {
  messageHtml: HtmlString
  taxRate: PricebookTaxRateDto
  dynamicPricingType?: DynamicPricingType
  options: Option[]
  photoRecords: EstimatePhoto[]
  maintenancePlanSavingsEnabled?: boolean
  hideLineItemPrices?: boolean
  pointOfContact?: EstimatePointOfContact
  location?: EstimateLocation
}

export const EstimateDataContext = React.createContext<EstimateDataContextType | undefined>(undefined)

type FetchedEstimatePhoto = {
  photoGuid: Guid
  cdnUrl: string
  fromJob?: boolean
}

type UploadedEstimatePhoto = FetchedEstimatePhoto & AsyncPhotoData

export type EstimatePhoto = FetchedEstimatePhoto | UploadedEstimatePhoto

export const isUploadedEstimatePhoto = (photo: EstimatePhoto): photo is UploadedEstimatePhoto =>
  !!(photo as UploadedEstimatePhoto).filename

type EstimateEditContextType = {
  setMessageHtml: StateSetter<HtmlString>
  setTaxRate: StateSetter<PricebookTaxRateDto>
  setDynamicPricingType: StateSetter<DynamicPricingType | undefined>
  onSetRecommended: (index: number, recommended?: boolean) => void
  setSelectedOptionIndex: StateSetter<number>
  shiftOption: (index: number, up: boolean) => void
  deleteOption: (index: number) => void
  duplicateOption: (index: number) => void
  setMaintenancePlanSavingsEnabled: StateSetter<boolean>
  setHideLineItemPrices: StateSetter<boolean>
  isDirty: boolean
  clearDirtyFlag: () => void
  status?: EstimateV2Status
  originalAcceptedOptionTotalUsc?: number
  photoRecords: EstimatePhoto[]
  setPhotoRecords: StateSetter<EstimatePhoto[]>
}

export const EstimateEditContext = React.createContext<EstimateEditContextType | undefined>(undefined)

export const useRelevantEstimateData = () => {
  const companyGuid = useExpectedCompanyGuid()

  // urql's `useQuery` will infuriatingly re-run when we do a mutation, which I really don't want because the whole page
  // will remount. So this tells it to cut it out after its initial fetch.
  const [isPaused, setIsPaused] = useState(false)

  const [{ data: relevantEstimateData, fetching: fetchingRelevantEstimateData }] = useQuery({
    query: RELEVANT_ESTIMATE_DATA_QUERY,
    variables: {
      companyGuid,
    },
    pause: isPaused,
  })

  useEffect(() => {
    if (relevantEstimateData) {
      setIsPaused(true)
    }
  }, [relevantEstimateData])

  const realPricebookItemGuidMap = useMemo(() => {
    const map: Record<Guid, true> = {}
    for (const pricebookItem of relevantEstimateData?.pricebookItems ?? []) {
      map[pricebookItem.pricebookItemGuid] = true
    }
    return map
  }, [relevantEstimateData?.pricebookItems])

  const defaultInvoiceTerm = useMemo(() => {
    const rawTerm = relevantEstimateData?.billingProfilesByPk?.invoiceTerm ?? InvoiceTerm.DUE_ON_RECEIPT
    return rawTerm !== InvoiceTerm.AUTO ? rawTerm : InvoiceTerm.DUE_ON_RECEIPT
  }, [relevantEstimateData?.billingProfilesByPk?.invoiceTerm])

  return {
    realPricebookItemGuidMap,
    companyDefaultTaxRateGuid: relevantEstimateData?.billingProfilesByPk?.defaultPricebookTaxRateGuid,
    disclaimer: relevantEstimateData?.billingProfilesByPk?.estimateDisclaimer ?? '',
    defaultInvoiceTerm,
    companyName: relevantEstimateData?.companiesByPk?.name ?? '',
    companyBlurb: relevantEstimateData?.companyConfigByPk?.blurb ?? DEFAULT_BLURB,
    companyMaintenancePlanSavingsEnabled:
      relevantEstimateData?.companyConfigByPk?.maintenancePlanSavingsEnabled ?? false,
    companyHideLineItemPrices: relevantEstimateData?.companyConfigByPk?.hideLineItemPrices ?? false,
    logoUrl: relevantEstimateData?.billingProfilesByPk?.logoUrl ?? '',
    logoPhotoGuid: relevantEstimateData?.billingProfilesByPk?.logoPhotoGuid ?? '',
    logoPhotoCdnUrl: relevantEstimateData?.billingProfilesByPk?.logoPhoto?.cdnUrl ?? '',
    isFetching: fetchingRelevantEstimateData,
  }
}

export type RelevantEstimateData = ReturnType<typeof useRelevantEstimateData>

export const convertEstimateOptionsToOptions = (
  estimateOptions: NonNullable<GetEstimateSubscription['estimatesByPk']>['estimateOptions'] | undefined,
  fallbackTaxConfiguration?: TaxConfiguration,
  noSort?: boolean,
) => {
  const options: Option[] = (estimateOptions || []).map(
    ({ estimateOptionDiscounts, cartItems, __typename, ...rest }) => {
      const taxConfiguration: TaxConfiguration = rest.taxConfiguration
        ? {
            roundingStrategy: rest.taxConfiguration.taxRoundingStrategy,
          }
        : (fallbackTaxConfiguration ?? {
            roundingStrategy: DEFAULT_TAX_ROUNDING_STRATEGY,
          })

      return {
        ...rest,
        taxConfiguration,
        featuredPhotoGuid: rest.featuredPhotoGuid,
        featuredPhotoCdnUrl: rest.featuredPhoto?.cdnUrl,
        discounts: estimateOptionDiscounts.map(
          ({ discountType, discountAmountUsc, discountRate, descriptionHtml, name, __typename, ...rest }) => {
            const common = {
              ...rest,
              name: name ?? '',
              descriptionHtml: descriptionHtml ?? '',
            }
            if (discountType === DiscountType.FLAT) {
              return {
                type: DiscountType.FLAT,
                // Though the typing thinks this could be undefined, we have DB-level checks that make sure if the type is
                // flat this isn't null
                discountAmountUsc: discountAmountUsc ?? 0,
                ...common,
              }
            } else {
              return {
                type: DiscountType.RATE,
                // Like above, we have DB-level checks that enforce that this won't be undefined if the type is rate
                discountRate: discountRate ?? 0,
                ...common,
              }
            }
          },
        ),
        lineItems: cartItems.map(
          ({ cartItem: { originalPricebookItemGuid, description, photo, __typename, ...rest }, seq }) => ({
            // The type needs to have a guid here. If it was an ad-hoc item this will be null, so we need to put some
            // fake nonsense in here.
            itemGuid: originalPricebookItemGuid ?? nextGuid(),
            description: description ?? '',
            seq,
            photoCdnUrl: photo?.cdnUrl,
            photoGuid: photo?.photoGuid,
            ...rest,
          }),
        ),
      }
    },
  )
  if (noSort) {
    return options
  }
  return R.sort(R.descend(R.prop('selected')), options)
}

export const useOptionsFromEstimateQuery = (
  estimateOptions: NonNullable<GetEstimateSubscription['estimatesByPk']>['estimateOptions'] | undefined,
  noSort?: boolean,
) => {
  const companyCurrentTaxConfiguration = useCompanyCurrentTaxConfiguration()
  return useMemo<Option[]>(() => {
    return convertEstimateOptionsToOptions(estimateOptions, companyCurrentTaxConfiguration, noSort)
  }, [companyCurrentTaxConfiguration, estimateOptions, noSort])
}

type SaveableEstimate = {
  estimateGuid: Guid
  status?: EstimateV2Status
  messageHtml?: HtmlString
  taxRate: PricebookTaxRateDto
  dynamicPricingType?: DynamicPricingType
  options: Option[]
  photoRecords: EstimatePhoto[]
  maintenancePlanSavingsEnabled?: boolean
  hideLineItemPrices?: boolean
}

export const useSaveEstimate = (existingDisplayId?: number) => {
  const companyGuid = useExpectedCompanyGuid()
  const user = usePrincipalUser()

  const { jobGuid, locationGuid, accountGuid, realPricebookItemGuidMap, jobAppointmentGuid } =
    useStrictContext(EstimatesContext)

  const [, upsertEstimate] = useMutation(UPSERT_ESTIMATE_MUTATION)
  const upsertRecentPricebookItems = useUpsertRecentPricebookItems()
  const { refetchRecentItems } = usePricebook()

  const generateDisplayIdMutation = trpc.invoice['invoicing:generate-display-id'].useMutation()

  const [isUpserting, setIsUpserting] = useState(false)
  const [displayId, setDisplayId] = useState<number | undefined>(existingDisplayId)

  const saveEstimate = useCallback(
    async ({
      status,
      messageHtml,
      options,
      estimateGuid,
      taxRate,
      dynamicPricingType,
      photoRecords,
      maintenancePlanSavingsEnabled,
      hideLineItemPrices,
    }: SaveableEstimate) => {
      // Shouldn't be possible
      const maintenancePlanSavingsFlag = maintenancePlanSavingsEnabled ?? false
      const hideLineItemPricesFlag = hideLineItemPrices ?? false
      if (!user.principal?.userGuid) {
        return false
      }
      setIsUpserting(true)

      try {
        const estimateOptionGuids: Guid[] = []
        const estimateOptionDiscountGuids: Guid[] = []
        const validCartItemGuids: Guid[] = []

        for (const option of options) {
          estimateOptionGuids.push(option.optionGuid)
          for (const { cartItemGuid } of option.lineItems) {
            validCartItemGuids.push(cartItemGuid)
          }
          for (const { discountGuid } of option.discounts) {
            estimateOptionDiscountGuids.push(discountGuid)
          }
        }

        const estimatePhotoGuids: Guid[] = []
        const nonJobPhotoGuids: Guid[] = []
        for (const photo of photoRecords) {
          estimatePhotoGuids.push(photo.photoGuid)
          if (!photo.fromJob) {
            nonJobPhotoGuids.push(photo.photoGuid)
          }
        }

        let ourDisplayId = displayId
        if (!ourDisplayId) {
          const newDisplayId = await generateDisplayIdMutation.mutateAsync({
            entityType: 'ESTIMATE',
          })
          ourDisplayId = newDisplayId
          setDisplayId(newDisplayId)
        }

        const validRecentPricebookItemGuids = options
          .flatMap(option => option.lineItems)
          .filter(item => realPricebookItemGuidMap[item.itemGuid])
          .map(item => item.itemGuid)

        try {
          if (validRecentPricebookItemGuids.length) {
            await upsertRecentPricebookItems(validRecentPricebookItemGuids)
            refetchRecentItems()
          }
        } catch (e) {
          console.error('[saveEstimate] Error upserting recent pricebook items', e)
        }

        const upsertRes = await upsertEstimate({
          estimateGuid,
          companyGuid,
          estimateOptionGuids,
          estimateOptionDiscountGuids,
          validCartItemGuids,
          estimatePhotoGuids,
          jobPhotoLinks: nonJobPhotoGuids.map(photoGuid => ({
            photoGuid,
            jobGuid,
            accountGuid,
            locationGuid,
          })),
          estimate: {
            jobGuid,
            jobAppointmentGuid: jobAppointmentGuid || undefined,
            companyGuid,
            estimateGuid,
            messageHtml,
            status,
            displayId: ourDisplayId,
            taxRate: taxRate.rate,
            taxRateName: taxRate.name,
            taxRateGuid: taxRate.pricebookTaxRateGuid,
            dynamicPricingType,
            maintenancePlanSavingsEnabled: maintenancePlanSavingsFlag,
            hideLineItemPrices: hideLineItemPricesFlag,
            createdBy: user.principal.userGuid,
            estimatePhotos: photoRecords.length
              ? {
                  onConflict: {
                    constraint: 'estimate_photos_pkey',
                    updateColumns: [],
                  },
                  data: estimatePhotoGuids.map(photoGuid => ({
                    companyGuid,
                    photoGuid,
                  })),
                }
              : undefined,
            estimateOptions: {
              onConflict: {
                constraint: 'estimate_options_pkey',
                updateColumns: [
                  'descriptionHtml',
                  'displayName',
                  'seq',
                  'isRecommended',
                  'isSelected',
                  'totalUsc',
                  'featuredPhotoGuid',
                ],
              },
              data: options.map<EstimateOptionsInsertInput>(
                (
                  {
                    descriptionHtml,
                    discounts,
                    displayName,
                    optionGuid,
                    lineItems,
                    recommended,
                    selected,
                    totalUsc,
                    featuredPhotoGuid,
                  },
                  i,
                ) => ({
                  companyGuid,
                  descriptionHtml,
                  displayName,
                  totalUsc,
                  estimateOptionGuid: optionGuid,
                  seq: i,
                  isRecommended: !!recommended,
                  isSelected: !!selected,
                  featuredPhotoGuid,
                  estimateOptionDiscounts: {
                    onConflict: {
                      constraint: 'estimate_option_discounts_pkey',
                      updateColumns: [
                        'discountAmountUsc',
                        'discountRate',
                        'discountType',
                        'descriptionHtml',
                        'name',
                        'seq',
                      ],
                    },
                    data: discounts.map(
                      ({ discountGuid, type, discountAmountUsc, discountRate, descriptionHtml, name }, i) => ({
                        companyGuid,
                        estimateOptionDiscountGuid: discountGuid,
                        discountType: type,
                        seq: i,
                        discountAmountUsc,
                        discountRate,
                        descriptionHtml,
                        name,
                      }),
                    ),
                  },
                  cartItems: {
                    onConflict: {
                      constraint: 'estimate_option_cart_items_pkey',
                      updateColumns: ['seq'],
                    },
                    data: lineItems.map(
                      ({
                        itemGuid,
                        itemType,
                        savedToPricebook,
                        seq,
                        photoCdnUrl,
                        cartItemVariant: variant,
                        ...rest
                      }) => ({
                        cartItem: {
                          onConflict: {
                            constraint: 'cart_items_pkey',
                            updateColumns: [
                              'name',
                              'description',
                              'quantity',
                              'unitPriceUsc',
                              'isTaxable',
                              'isDiscountable',
                              'cartItemType',
                              'originalPricebookItemGuid',
                              'photoGuid',
                              'cartItemVariant',
                            ],
                          },
                          data: {
                            ...rest,
                            companyGuid,
                            // If it's an ad-hoc item it gets a random guid, so we need to check if it's real
                            originalPricebookItemGuid: realPricebookItemGuidMap[itemGuid] ? itemGuid : undefined,
                            cartItemType: itemType,
                            cartItemVariant: variant,
                          },
                        },
                        seq,
                      }),
                    ),
                  },
                }),
              ),
            },
          },
        })
        if (upsertRes.error) {
          console.error('[saveEstimate] Upsert error:', upsertRes.error)
          setIsUpserting(false)
          throw upsertRes.error
        }

        setIsUpserting(false)
        return true
      } catch (e) {
        console.error('[saveEstimate] Error in save:', e)
        setIsUpserting(false)
        return false
      }
    },
    [
      accountGuid,
      companyGuid,
      displayId,
      generateDisplayIdMutation,
      jobAppointmentGuid,
      jobGuid,
      locationGuid,
      realPricebookItemGuidMap,
      refetchRecentItems,
      upsertEstimate,
      upsertRecentPricebookItems,
      user.principal?.userGuid,
    ],
  )

  return [saveEstimate, isUpserting] as const
}

export const getDuplicatedOption = ({ optionGuid, lineItems, discounts, ...restOption }: Option): Option => ({
  ...restOption,
  optionGuid: nextGuid(),
  lineItems: lineItems.map(({ cartItemGuid, ...restItem }) => ({
    cartItemGuid: nextGuid(),
    ...restItem,
  })),
  discounts: discounts.map(({ discountGuid, ...restDiscount }) => ({
    discountGuid: nextGuid(),
    ...restDiscount,
  })),
  selected: false,
  recommended: false,
})

export const useAnyFinanceable = (options: Option[]) => {
  const wisetackEnabled = useWisetackEnabled()

  const anyFinanceable = useMemo<boolean>(
    () => (wisetackEnabled ?? false) && options.some(o => !!isFinanceableAmountUsd(o.totalUsc)),
    [wisetackEnabled, options],
  )

  return anyFinanceable
}

export const useIsFinanceableCart = (totalPriceUsc: number) => {
  const wisetackEnabled = useWisetackEnabled()

  const isFinanceable = useMemo<boolean>(
    () => (wisetackEnabled ?? false) && !!isFinanceableAmountUsd(totalPriceUsc),
    [wisetackEnabled, totalPriceUsc],
  )

  return isFinanceable
}

type EstimateV2StatusMetadata = {
  label: string
  statusTagColor: StatusTagColor
  colorClassNames: string
  markAsOptions?: EstimateV2Status[]
  canDelete?: boolean
  hasSpecialMarkAsBehavior?: boolean
  // If we try to set an estimate to this status, but it's already in one of the statuses in this array, we don't do it.
  trumpedBy?: EstimateV2Status[]
}

const ESTIMATE_V2_STATUS_DISPLAY_INFO = {
  DRAFT: {
    label: 'Draft',
    colorClassNames: 'text-bz-gray-900 bg-bz-gray-400',
    statusTagColor: 'darkGray',
    canDelete: true,
  },
  CREATED: {
    label: 'Created',
    colorClassNames: 'text-cyan-900 bg-cyan-200',
    statusTagColor: 'cyan',
    markAsOptions: ['SENT', 'REVIEWED', 'ACCEPTED', 'VOIDED', 'CLOSED'],
    canDelete: true,
  },
  SENT: {
    label: 'Sent',
    colorClassNames: 'text-geek-blue-900 bg-geek-blue-200',
    statusTagColor: 'blue',
    markAsOptions: ['REVIEWED', 'ACCEPTED', 'VOIDED', 'CLOSED'],
    trumpedBy: ['REVIEWED', 'ACCEPTED', 'VOIDED', 'CLOSED', 'EXPIRED'],
  },
  REVIEWED: {
    label: 'Reviewed',
    colorClassNames: 'text-purple-900 bg-purple-200',
    statusTagColor: 'purple',
    markAsOptions: ['CREATED', 'SENT', 'ACCEPTED', 'VOIDED', 'CLOSED'],
    trumpedBy: ['ACCEPTED', 'VOIDED', 'CLOSED', 'EXPIRED'],
  },
  ACCEPTED: {
    label: 'Accepted',
    colorClassNames: `text-bz-green-900 bg-bz-green-200`,
    statusTagColor: 'green',
    markAsOptions: ['VOIDED', 'CLOSED'],
    hasSpecialMarkAsBehavior: true,
  },
  VOIDED: {
    label: 'Voided',
    colorClassNames: 'text-bz-red-900 bg-bz-red-200',
    statusTagColor: 'red',
  },
  CLOSED: {
    label: 'Closed - Lost',
    colorClassNames: 'text-bz-gray-900 bg-bz-gray-500',
    statusTagColor: 'darkGray',
    markAsOptions: ['ACCEPTED', 'VOIDED'],
  },
  EXPIRED: {
    label: 'Expired',
    colorClassNames: 'text-bz-gray-900 bg-bz-gray-400',
    statusTagColor: 'darkGray',
    markAsOptions: ['CREATED', 'SENT', 'REVIEWED', 'ACCEPTED', 'VOIDED', 'CLOSED'],
  },
} satisfies Record<EstimateV2Status, EstimateV2StatusMetadata>

export const getEstimateV2StatusDisplayInfo = (status: EstimateV2Status): EstimateV2StatusMetadata =>
  ESTIMATE_V2_STATUS_DISPLAY_INFO[status]

type SpecialMarkAsStatus = {
  [K in keyof typeof ESTIMATE_V2_STATUS_DISPLAY_INFO]: (typeof ESTIMATE_V2_STATUS_DISPLAY_INFO)[K] extends {
    hasSpecialMarkAsBehavior: true
  }
    ? K
    : never
}[keyof typeof ESTIMATE_V2_STATUS_DISPLAY_INFO]

export const hasSpecialMarkAsBehavior = (status: EstimateV2Status): status is SpecialMarkAsStatus =>
  !!ESTIMATE_V2_STATUS_DISPLAY_INFO[status as SpecialMarkAsStatus].hasSpecialMarkAsBehavior

export const DEFAULT_ESTIMATE_OPTION_NAME = 'Option'

export const CAROUSEL_ITEM_WIDTH = 660

// Use to update the status of the estimate. Useful for things like "setting the estimate to 'Sent' when they send". It
// verifies that you aren't setting it "backwards" either: if they have already "Reviewed" it and then send it, we don't
// set it to "Sent" because "Reviewed" trumps "Sent". The function takes an optional second argument "force", however,
// and if set we skip that check (in case you literally want to set it to exactly what you're choosing, necessary for a
// "Change Status" control where backwards moves are valid).
export const useEstimateStatusUpdater = (currentStatus?: EstimateV2Status) => {
  const { estimateGuid } = useStrictContext(EstimatesContext)
  const [{ fetching }, updateEstimate] = useMutation(UPDATE_ESTIMATE_MUTATION)

  const estimateStatusUpdater = useCallback(
    async (newStatus: EstimateV2Status, force?: boolean) => {
      if (!currentStatus) {
        return
      }
      if (newStatus === currentStatus) {
        return
      }
      if (!force && getEstimateV2StatusDisplayInfo(newStatus).trumpedBy?.includes(currentStatus)) {
        return
      }
      let clearSelectedOptionsEstimateGuid = DUMMY_GUID
      if (currentStatus === 'ACCEPTED') {
        clearSelectedOptionsEstimateGuid = estimateGuid
      }
      await updateEstimate({
        estimateGuid,
        estimate: {
          status: newStatus,
        },
        clearSelectedOptionsEstimateGuid,
      })
    },
    [currentStatus, estimateGuid, updateEstimate],
  )

  return [estimateStatusUpdater, fetching] as const
}

export const useEstimateMaintenancePlanSavingsUpdater = () => {
  const { estimateGuid } = useStrictContext(EstimatesContext)
  const [{ fetching }, updateEstimate] = useMutation(UPDATE_ESTIMATE_MUTATION)

  const updateMaintenancePlanSavingsEnabled = useCallback(
    async (enabled: boolean) => {
      const result = await updateEstimate({
        estimateGuid,
        estimate: {
          maintenancePlanSavingsEnabled: enabled,
        },
        clearSelectedOptionsEstimateGuid: DUMMY_GUID,
      })

      if (result.error) {
        throw result.error
      }
    },
    [estimateGuid, updateEstimate],
  )

  return [updateMaintenancePlanSavingsEnabled, fetching] as const
}

export const useEstimateHideLineItemPricesUpdater = () => {
  const { estimateGuid } = useStrictContext(EstimatesContext)
  const [{ fetching }, updateEstimate] = useMutation(UPDATE_ESTIMATE_MUTATION)

  const updateHideLineItemPrices = useCallback(
    async (enabled: boolean) => {
      const result = await updateEstimate({
        estimateGuid,
        estimate: {
          hideLineItemPrices: enabled,
        },
        clearSelectedOptionsEstimateGuid: DUMMY_GUID,
      })

      if (result.error) {
        throw result.error
      }
    },
    [estimateGuid, updateEstimate],
  )

  return [updateHideLineItemPrices, fetching] as const
}

export const isEstimateCreatePath = (pathname: string) =>
  !!pathname.match(
    /.*\/jobs\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\/new-estimate$/,
  )

export const isEstimateOverviewPath = (pathname: string) =>
  !!pathname.match(
    /.*\/estimates\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\/?$/,
  )

export const useUnauthDownloadEstimate = (estimateGuid: Guid) => {
  // Use the new unauthenticated PDF download endpoint for customer access
  const unauthDownloadPdfMutation = trpc.invoice['unauth:invoicing:estimatev2:download-pdf'].useMutation()
  const utils = trpc.useUtils()
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const onDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true)
    try {
      // Get the estimate data (for display ID)
      const data = await utils.invoice['unauth:invoicing:get-estimate-for-download'].fetch({ estimateGuid })

      // Use the unauthenticated backend PDF service
      const response = await unauthDownloadPdfMutation.mutateAsync({
        estimateGuid,
      })

      // Convert base64 to blob
      const binaryString = atob(response.estimatePdfBase64)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const pdfBlob = new Blob([bytes], { type: 'application/pdf' })

      // Trigger download
      const pdfBlobUrl = URL.createObjectURL(pdfBlob)
      const downloadLink = document.createElement('a')
      downloadLink.href = pdfBlobUrl
      downloadLink.download = `estimate-${data?.displayId}.pdf`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)

      // Clean up
      URL.revokeObjectURL(pdfBlobUrl)
    } catch (error) {
      console.error('Failed to download PDF:', error)
      throw error
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [estimateGuid, utils, unauthDownloadPdfMutation])

  return {
    onDownloadPdf,
    estimateDownloading: isGeneratingPdf || unauthDownloadPdfMutation.isPending,
  }
}

export const DownloadPdfButton = ({ estimateGuid }: { estimateGuid: Guid }) => {
  const { onDownloadPdf, estimateDownloading } = useUnauthDownloadEstimate(estimateGuid)
  const isMobile = useIsMobile()

  return (
    <Button
      disabled={estimateDownloading}
      className={classNames(
        'flex cursor-pointer flex-row items-center gap-1',
        estimateDownloading ? 'pointer-events-none cursor-wait' : '',
        {
          'review-estimate-page-preview-back-button-drop-shadow w-9 justify-center rounded-full': isMobile,
        },
      )}
      onClick={onDownloadPdf}
    >
      <div className="flex items-center justify-center">
        {estimateDownloading ? <BaseLoadingSpinner size={4} /> : <FontAwesomeIcon icon={faDownload} />}{' '}
      </div>
      {isMobile ? null : <div className="flex-1">Download PDF</div>}
    </Button>
  )
}
